import React, { useState, useEffect, useRef } from "react";
import { TextField, Grid } from "@mui/material";
import CachedIcon from '@mui/icons-material/Cached';

const Captcha = ({ onChange, captchaText, setCaptchaText }) => {
  const [canvasPattern, setCanvasPattern] = useState("");
  const [canvasKey, setCanvasKey] = useState(0); // For refreshing canvas
  const canvasRef = useRef(null);

  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    generatePattern();
  }, [captchaText]);

  const generateCaptcha = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const combiningChars = [
      '\u0300', '\u0301', '\u0302', '\u0303', '\u0304', '\u0305',
      '\u0306', '\u0307', '\u0308', '\u0309', '\u030A', '\u030B',
      '\u030C', '\u030D', '\u030E', '\u030F', '\u0310', '\u0311',
      '\u0312', '\u0313', '\u0314', '\u0315', '\u0316', '\u0317',
      '\u0318', '\u0319', '\u031A', '\u031B', '\u031C', '\u031D',
      '\u031E', '\u031F', '\u0320', '\u0321', '\u0322', '\u0323',
      '\u0324', '\u0325', '\u0326', '\u0327', '\u0328', '\u0329',
      '\u032A', '\u032B', '\u032C', '\u032D', '\u032E', '\u032F',
      '\u0330', '\u0331', '\u0332', '\u0333', '\u0334', '\u0335',
      '\u0336', '\u0337', '\u0338', '\u0339', '\u033A', '\u033B',
      '\u033C', '\u033D', '\u033E', '\u033F', '\u0340', '\u0341',
      '\u0342', '\u0343', '\u0344', '\u0345', '\u0346', '\u0347',
      '\u0348', '\u0349', '\u034A', '\u034B', '\u034C', '\u034D',
      '\u034E', '\u034F', '\u0350', '\u0351', '\u0352', '\u0353',
      '\u0354', '\u0355', '\u0356', '\u0357', '\u0358', '\u0359',
      '\u035A', '\u035B', '\u035C', '\u035D', '\u035E', '\u035F',
      '\u0360', '\u0361', '\u0362', '\u0363', '\u0364', '\u0365',
      '\u0366', '\u0367', '\u0368', '\u0369', '\u036A', '\u036B',
      '\u036C', '\u036D', '\u036E', '\u036F'
    ];
    const length = Math.floor(Math.random() * 5) + 4; // Random length between 4 and 8
    let text = "";
    for (let i = 0; i < length; i++) {
      const char = chars.charAt(Math.floor(Math.random() * chars.length));
      const combineChar1 = combiningChars[Math.floor(Math.random() * combiningChars.length)];
      const combineChar2 = combiningChars[Math.floor(Math.random() * combiningChars.length)];
      text += char + combineChar1 + combineChar2;
    }
    setCaptchaText(text);
  };

  const generatePattern = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const width = 200;
    const height = 80;

    canvas.width = width;
    canvas.height = height;

    // Generate random background color (light colors only)
    const backgroundColor = getRandomLightColor();
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);

    // Draw random background pattern
    const patternType = Math.floor(Math.random() * 3);
    switch (patternType) {
      case 0:
        drawZigzagPattern(ctx, width, height);
        break;
      case 1:
        drawDotsPattern(ctx, width, height);
        break;
      case 2:
        drawLinesPattern(ctx, width, height);
        break;
      default:
        drawZigzagPattern(ctx, width, height);
    }

    // Draw captcha text
    ctx.font = "30px cursive"; // Adjust font size and style
    ctx.fillStyle = getTextColor(backgroundColor); // Adjust text color based on background
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Apply random rotation to each character
    const chars = captchaText.split('');
    const step = width / (chars.length + 1);
    chars.forEach((char, index) => {
      const charX = (index + 1) * step;
      const charY = height / 2;
      const angle = (Math.random() - 0.5) * Math.PI / 4; // Random angle between -22.5 to 22.5 degrees

      ctx.save();
      ctx.translate(charX, charY);
      ctx.rotate(angle);
      ctx.fillText(char, 0, 0);
      ctx.restore();
    });

    const pattern = canvas.toDataURL();
    setCanvasPattern(pattern);
  };

  const handleRefresh = () => {
    generateCaptcha();
    setCanvasKey((prevKey) => prevKey + 1); // Force re-render of canvas
  };

  const getRandomLightColor = () => {
    const letters = "CDEF"; // Only light colors
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * letters.length)];
    }
    return color;
  };

  const getTextColor = (backgroundColor) => {
    // Calculate RGB values from hex
    const rgb = hexToRgb(backgroundColor);
    // Make the text color significantly darker
    const darkerRgb = {
      r: Math.max(0, rgb.r - 100),
      g: Math.max(0, rgb.g - 100),
      b: Math.max(0, rgb.b - 100),
    };
    return `rgb(${darkerRgb.r}, ${darkerRgb.g}, ${darkerRgb.b})`;
  };

  const hexToRgb = (hex) => {
    // Convert hex color to RGB
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = (bigint & 255);
    return { r, g, b };
  };

  const drawZigzagPattern = (ctx, width, height) => {
    ctx.strokeStyle = "#ccc";
    ctx.lineWidth = 1;
    const zigzagSpacing = 15;
    const zigzagHeight = 40;
    const zigzagCount = Math.ceil(width / zigzagSpacing);

    ctx.beginPath();
    ctx.moveTo(0, height / 2);

    for (let i = 0; i < zigzagCount; i++) {
      ctx.lineTo(i * zigzagSpacing + zigzagSpacing / 2, height / 2 + (i % 2 === 0 ? -zigzagHeight : zigzagHeight));
    }

    ctx.stroke();
  };

  const drawDotsPattern = (ctx, width, height) => {
    ctx.fillStyle = "#aaa";
    const dotCount = 100;
    for (let i = 0; i < dotCount; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const radius = Math.random() * 3;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const drawLinesPattern = (ctx, width, height) => {
    ctx.strokeStyle = "#bbb";
    ctx.lineWidth = 2;
    const lineCount = 10;
    for (let i = 0; i < lineCount; i++) {
      const startX = Math.random() * width;
      const startY = Math.random() * height;
      const endX = Math.random() * width;
      const endY = Math.random() * height;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    }
  };

  return (
    <Grid container alignItems="center" spacing={2} sx={{marginTop:"15px"}}>
      <Grid item xs={7}>
        <TextField
          id="captcha"
          name="captcha"
          label="Enter CAPTCHA"
          fullWidth
          variant="standard"
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={5}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <canvas key={canvasKey} ref={canvasRef} style={{ height: "38px", width: "100%" }} />
          <CachedIcon variant="outlined" onClick={handleRefresh}></CachedIcon>
        </div>
      </Grid>
    </Grid>
  );
};

export default Captcha;
